/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drug management landing page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Link } from 'react-router-dom';

import { useParams, useHistory } from 'react-router';

import Typography from '@material-ui/core/Typography';

import { Theme, makeStyles } from '@material-ui/core/styles';

import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

import { DateTime } from 'luxon';

import { Button, Grid, Paper } from '@material-ui/core';

import { Column, Row } from 'react-table';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { InstitutionsContext, InstitutionSelector, usePatients, CollapsibleTable } from '@ngt/online-patient-management-ui';

import { ResponseStatus } from '@servicestack/client';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';
import { usePharmacies } from '../../store/modules/drugmanagement/pharmacies';
import PharmacyModal from './Modals/PharmacyModal';
import { useDrugs } from '../../store/modules/drugmanagement/drugs';
import { useDepots } from '../../store/modules/drugmanagement/depots';
import { usePharmacy } from '../../store/modules/drugmanagement/pharmacy';
import { useDrugShipments } from '../../store/modules/drugmanagement/drugshipments';
import { useDrugShipment, DrugShipmentActions } from '../../store/modules/drugmanagement/drugshipment';
import DrugShipmentModal from './Modals/DrugShipmentModal';
import { useDepotBatches, DepotBatchesActions } from '../../store/modules/drugmanagement/depotbatches';
import { getShipmentStatusText } from '../../utilities/getShipmentStatusText';
import DrugShipmentStatusModal from './Modals/DrugShipmentStatusModal';
import DrugBreadcrumbs from '../../components/Breadcrumbs/DrugBreadcrumbs';
import { usePharmacists } from '../../store/modules/drugmanagement/pharmacists';
import DrugShipmentDeleteModal from './Modals/DrugShipmentDeleteModal';
import { getDrugRemaining } from '../../utilities/drugShipmentDepotBatchCalculators';
import DrugShipmentReceivedModal from './Modals/DrugShipmentReceivedModal';
import DrugShipmentTableButtons from './Components/DrugShipmentTableButtons';
import { useDrugShipmentVerify } from '../../store/modules/drugmanagement/drugshipmentverify';
import { usePatientsDrugs } from '../../store/modules/drugmanagement/patientsdrugs';
import drugManagementSettings from '../../constants/drugManagementSettings';
import { getPharmacyStatusText } from '../../utilities/getPharmacyStatusText';
import { useDrugDispensations } from '../../store/modules/drugmanagement/drugdispensations';
import { useSnackbar } from 'notistack';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugManagementPageParams {
    countryCode?: string;
    institutionCode?: string;
}

interface IDrugManagementPageProps {
}

interface IPharmacyWithDrugCount extends Dtos.Pharmacy {
    nivoCount: number;
    nivoRelaCount: number
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    buttonSet: {
        textAlign: 'right',
        '& > *': {
            marginLeft: '1rem'
        }
    },
    button: {
        marginLeft: theme.spacing(3),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    },
    tables: {
        '& .Mui-expanded:first-child': {
            marginTop: '1rem'
        },

        '& .MuiPagination-ul': {
            justifyContent: 'right'
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const usePharmacyColumns = () => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<IPharmacyWithDrugCount>> = [
            {
                Header: 'Pharmacy Name',
                accessor: 'pharmacyName',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Status',
                id: 'pharmactStatus',
                accessor: (p) => getPharmacyStatusText(p.pharmacyStatus),
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Nivolumab/Relatlimab FDC',
                accessor: 'nivoRelaCount',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Adjuvant Nivolumab',
                accessor: 'nivoCount',
                minWidth: 155,
                maxWidth: 155
            }
        ]

        return cols;
    }, []);

    return columns;
};

const getShipmentStatusDate = (drugShipment: Dtos.DrugShipment): string => {
    const statusDate = drugShipment
        .drugShipmentStatusHistory[drugShipment.drugShipmentStatusHistory.length - 1]
        .statusDate;

    return statusDate;
};

const getPharmaciesWithDrugCount = (pharmacies: Dtos.Pharmacy[],
    drugShipments: Dtos.DrugShipment[] | null,
    drugShipmentLoadState: IRequestState<ResponseStatus>): IPharmacyWithDrugCount[] => {
    const pharmaciesWithDrugCount: IPharmacyWithDrugCount[] = [];

    if (pharmacies &&
        drugShipmentLoadState &&
        drugShipmentLoadState.state !== RequestState.Success) {
        pharmacies.forEach(pharmacy => {
            pharmaciesWithDrugCount.push({ ...pharmacy, nivoCount: 0, nivoRelaCount: 0});
        });
    }

    if (pharmacies &&
        drugShipmentLoadState &&
        drugShipmentLoadState.state === RequestState.Success) {
        pharmacies.forEach(pharmacy => {
            const nivoCount = drugShipments ?
                drugShipments
                    .filter(ds => ds.pharmacyId === pharmacy.id &&
                        ds.shipmentStatus === Dtos.ShipmentStatus.Available)
                    .map(drugShipment => drugShipment.drugShipmentDepotBatches?.filter(x => (x.drugId ?? x.depotBatch?.batch.drugId) === Dtos.DrugTypes.AdjuvantNivolumab))
                    .reduce((accumulator, value) => accumulator.concat(value), [])
                    .reduce((accumulator, value) => accumulator + (getDrugRemaining(value)), 0) :
                0;

            const nivoRelaCount = drugShipments ?
                drugShipments
                    .filter(ds => ds.pharmacyId === pharmacy.id &&
                        ds.shipmentStatus === Dtos.ShipmentStatus.Available)
                    .map(drugShipment => drugShipment.drugShipmentDepotBatches?.filter(x => (x.drugId ?? x.depotBatch?.batch.drugId) === Dtos.DrugTypes.NivolumabRelatlimab))
                    .reduce((accumulator, value) => accumulator.concat(value), [])
                    .reduce((accumulator, value) => accumulator + (getDrugRemaining(value)), 0) :
                0;

            pharmaciesWithDrugCount.push({ ...pharmacy, nivoCount, nivoRelaCount });
        });
    }

    return pharmaciesWithDrugCount;
}

const useDrugShipmentColumns = (pharmacies: Dtos.Pharmacy[] | null,
    setDrugShipmentId: React.Dispatch<React.SetStateAction<number | null>>,
    setDrugShipmentStatus: React.Dispatch<React.SetStateAction<Dtos.ShipmentStatus | null>>,
    setDrugShipmentStatusModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setDrugShipmentReceiveModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setDrugShipmentDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    canAdministerDrugManagement: boolean | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.DrugShipment>> = [
            {
                Header: 'Pharmacy Name',
                id: 'pharmacyName',
                accessor: (drugShipment) => pharmacies?.find(p => p.id === drugShipment.pharmacyId)?.pharmacyName,
            },
            {
                Header: 'Last Update',
                id: 'lastUpdate',
                accessor: drugShipment => getShipmentStatusDate(drugShipment),
                Cell: ({ cell: { value }}: any) => DateTime.fromISO(value).toFormat('dd/MM/yyyy'),
                minWidth: 100,
                maxWidth: 100
            },
            {
                Header: drugManagementSettings.consignmentNumberLabel,
                accessor: 'consignmentNo',
                minWidth: 100,
                maxWidth: 100
            },
            {
                Header: 'Status',
                id: 'pharmacyStatus',
                accessor: (drugShipment) => getShipmentStatusText(drugShipment.shipmentStatus!),
                minWidth: 100,
                maxWidth: 100
            },
            {
                Header: 'Nivolumab / Relatlimab FDC Vials',
                id: 'nivolumabRelatlimab',
                accessor: (drugShipment) => drugShipment.drugShipmentDepotBatches.filter(x => (x.drugId ?? x.depotBatch?.batch.drugId) === Dtos.DrugTypes.NivolumabRelatlimab)
                    .map(x => x.drugUnits)
                    .reduce((accumulator, value) => accumulator! + value!, 0),
                minWidth: 130,
                maxWidth: 130
            },
            {
                Header: 'Adjuvant Nivolumab Vials',
                id: 'nivolumab',
                accessor: (drugShipment) => drugShipment.drugShipmentDepotBatches.filter(x => (x.drugId ?? x.depotBatch?.batch.drugId) === Dtos.DrugTypes.AdjuvantNivolumab)
                    .map(x => x.drugUnits)
                    .reduce((accumulator, value) => accumulator! + value!, 0),
                minWidth: 100,
                maxWidth: 100
            },
            {
                Header: 'Actions',
                id: 'actions',
                disableSortBy: true,
                Cell: (props: any) => {
                    return <div style={{ display: 'flex' }}>
                        {
                            DrugShipmentTableButtons({
                                drugShipmentId: props.row.original.id,
                                pharmacyId: props.row.original.pharmacyId,
                                shipmentStatus: props.row.original.shipmentStatus,
                                setDrugShipmentId,
                                setDrugShipmentStatus,
                                setDrugShipmentStatusModalOpen,
                                setDrugShipmentReceiveModalOpen,
                                setDrugShipmentDeleteModalOpen,
                                canAdministerDrugManagement
                            })
                        }
                    </div>
                }
            } as any
        ];

        return cols;
    },
    [
        pharmacies,
        setDrugShipmentId,
        setDrugShipmentStatus,
        setDrugShipmentStatusModalOpen,
        setDrugShipmentReceiveModalOpen,
        setDrugShipmentDeleteModalOpen,
        canAdministerDrugManagement
    ]);

    return columns;
};

const usePatientColumns = (patients: Dtos.Patient[] | null,
    institutions: Dtos.IInstitution[] | null,
    patientsDrugs: Dtos.PatientDrug[] | null,
    drugDispensations: Dtos.DrugDispensation[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Patient>> = [
            {
                Header: 'Patient Number',
                accessor: 'studyNumber',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Institution',
                id: 'institution',
                accessor: (patient) => institutions?.find(i => i.id === patient.institutionId)?.name,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Cohort',
                id: 'treatment',
                accessor: (patient) => patient.treatmentId === 1 ? 'Arm A' : (patient.treatmentId === 2 ? 'Arm B' : 'Arm C'),
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Nivolumab / Relatlimab FDC Treatment Status',
                id: 'nivoRelaTreatmentStatus',
                accessor: (patient) => {
                    const patientDrug = patientsDrugs?.find(pd => pd.patientId === patient.id &&
                        pd.drugId === Dtos.DrugTypes.NivolumabRelatlimab);

                    return patientDrug?.patientTreatmentStatus === Dtos.PatientTreatmentStatus.OnTreatment ? <Typography color="secondary">On Treatment</Typography> : "Off Treatment";
                }
            },
            {
                Header: 'Adjuvant Nivolumab Treatment Status',
                id: 'nivoTreatmentStatus',
                accessor: (patient) => {
                    const patientDrug = patientsDrugs?.find(pd => pd.patientId === patient.id &&
                        pd.drugId === Dtos.DrugTypes.AdjuvantNivolumab);

                    return patientDrug?.patientTreatmentStatus === Dtos.PatientTreatmentStatus.OnTreatment ? <Typography color="secondary">On Treatment</Typography> : "Off Treatment";
                }
            },
            {
                Header: 'Last Cycle Dispensed',
                id: 'id',
                accessor: (patient) => {
                    const patientDrugDispensations = drugDispensations?.filter(x => x.patientId === patient.id)?.sort((a, b) => { return a.id! > b.id! ? -1 : 1; });

                    if ((patientDrugDispensations?.length ?? 0) > 0) {
                        const lastDispensation = patientDrugDispensations![0];

                        return lastDispensation.timepoint.value
                    }

                    return '';
                }
            }
        ];

        return cols;
    }, [institutions, patientsDrugs, drugDispensations]);

    return columns;
};

const useDepotColumns = () => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Depot>> = [
            {
                Header: 'Depot Name',
                accessor: 'depotName',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Address',
                accessor: 'address',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Email',
                accessor: 'email',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Contact Name',
                accessor: 'contactName',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Phone Number',
                accessor: 'phoneNo',
                minWidth: 155,
                maxWidth: 155
            }
        ]

        return cols;
    }, []);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugManagementPermissions = [Dtos.Permission.DrugManagementAdminister, Dtos.Permission.DrugManagementView];

const TREATMENT_ARMC_ID = 3;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */


const DrugManagementPage: React.FunctionComponent<IDrugManagementPageProps> = () => {
    const classes = useStyles();

    const history = useHistory();

    const params = useParams<IDrugManagementPageParams>();

    const [institutions, institutionsLoadState, ] = React.useContext(InstitutionsContext);

    const [[canAdministerDrugManagement, canViewDrugManagement], drugManagementPermissionLoadState] = useBctApiPermissions(drugManagementPermissions);

    const [patients, patientsLoadState, patientsActions] = usePatients(
        undefined,
        undefined,
        undefined,
        params.institutionCode
    );

    const [drugs, drugLoadState, drugActions] = useDrugs();

    const [depots, depotsLoadState, depotsActions] = useDepots();

    const [depotBatches, depotBatchesLoadState, depotBatchesActions] = useDepotBatches();

    const [pharmacies, pharmaciesLoadState, pharmaciesActions] = usePharmacies();

    const [pharmacists, pharmacistsLoadState, pharmacistsActions] = usePharmacists();

    const [, , , pharmacyActions] = usePharmacy();

    const [, , drugShipmentVerifyActions] = useDrugShipmentVerify();

    const [drugShipments, drugShipmentsLoadState, drugShipmentsActions] = useDrugShipments();

    const [patientsDrugs, , patientsDrugsActions] = usePatientsDrugs();

    const [drugDispensations, , drugDispensationsActions] = useDrugDispensations();

    const [drugShipmentId, setDrugShipmentId] = React.useState<number | null>(null);

    const [drugShipmentForm, setDrugShipmentForm] = React.useState<Dtos.DrugShipment | null>(null);

    const [pharmacyForm, setPharmacyForm] = React.useState<Dtos.Pharmacy | null>(null);

    const [newShipmentStatus, setDrugShipmentStatus] = React.useState<Dtos.ShipmentStatus | null>(null);

    const [pharmacyModalOpen, setPharmacyModalOpen] = React.useState(false);

    const [drugShipmentModalOpen, setDrugShipmentModalOpen] = React.useState(false);

    const [drugShipmentStatusModalOpen, setDrugShipmentStatusModalOpen] = React.useState(false);

    const [drugShipmentDeleteModalOpen, setDrugShipmentDeleteModalOpen] = React.useState(false);

    const [drugShipmentReceivedModalOpen, setDrugShipmentReceivedModalOpen] = React.useState(false);

    // filter institutions

    const institutionsToUse = React.useMemo(() => {
        return institutions?.filter(i => (i as Dtos.Institution).activatedDate != null) ?? []
    }, [institutions]);


    //filter data based on selected institution

    const patientsToUse = React.useMemo(() => {
        if (patients && institutions) {
            var patientsToUse = patients
                .filter(p => institutions.find(i => i.id === p.institutionId))
                .filter(p => p.status === Dtos.PatientStatuses.Registered || p.status === Dtos.PatientStatuses.Randomised) as Dtos.Patient[];

            return patientsToUse.filter(p => p.treatmentId === TREATMENT_ARMC_ID);
        }

        return null;
    }, [patients, institutions]);

    const pharmaciesToUse = React.useMemo(() => {
        if (pharmacies) {
            if (params.institutionCode) {
                return pharmacies?.filter(p => p.institutionCode === params.institutionCode);
            }
            else {
                return pharmacies;
            }
        }

        return null;
    }, [pharmacies, params.institutionCode]);

    const drugShipmentsToUse = React.useMemo(() => {
        if (drugShipments) {
            if (params.institutionCode) {
                return [...drugShipments].filter(d => d.institutionCode === params.institutionCode)
                    .sort((a, b) => {
                        return getShipmentStatusDate(a) > getShipmentStatusDate(b) ?
                            -1 :
                            1;
                    });
            }
            else {
                return [...drugShipments].sort((a, b) => {
                    return getShipmentStatusDate(a) > getShipmentStatusDate(b) ?
                        -1 :
                        1;
                });
            }
        }

        return null;
    }, [drugShipments, params.institutionCode]);

    const depotToUse = depots?.filter(depot => depot.active) ?? [];

    const convertedPharmacies = React.useMemo(() => {
        if (pharmaciesToUse) {
            return getPharmaciesWithDrugCount(pharmaciesToUse,
                drugShipmentsToUse,
                drugShipmentsLoadState);
        }

        return null;
    }, [pharmaciesToUse, drugShipmentsToUse, drugShipmentsLoadState]);

    const pharmacyDrugs = React.useMemo(() => {
        if (drugShipmentForm) {
            return pharmacies?.find(x => x.institutionCode === drugShipmentForm.institutionCode)?.pharmacyDrugs ?? [];
        }

        return [];
    }, [pharmacies, drugShipmentForm]);

    const institutionsLoading = institutionsLoadState.state === RequestState.None || institutionsLoadState.state === RequestState.Pending;

    React.useEffect(() => {
        if (drugManagementPermissionLoadState.state !== RequestState.Pending &&
            drugManagementPermissionLoadState.state !== RequestState.None) {
            if (!canViewDrugManagement) {
                //i'm putting this here explicitly because we don't have any permissions on who can go to what route at the moment
                //on the off chance the BCT add a link directly to the drug management page, we want to send people who can't see
                //drug management to the 403 page
                history.replace(`/error/403`);
            }
            else if (institutions && institutions?.length === 1) {
                //just send the patient to their matching institution if they only have one
                history.replace(`/drug-management/${institutions[0].code}`);
            }
        }
    }, [institutions, canViewDrugManagement, drugManagementPermissionLoadState, history]);

    React.useEffect(() => {
        drugActions.load();

        return () => {
            drugActions.clear();
        };
    }, [drugActions]);

    React.useEffect(() => {
        depotsActions.load();

        return () => {
            depotsActions.clear();
        };
    }, [depotsActions]);

    React.useEffect(() => {
        pharmaciesActions.load();

        return () => {
            pharmaciesActions.clear();
        }
    }, [pharmaciesActions]);

    React.useEffect(() => {
        drugShipmentsActions.load();

        return () => {
            drugShipmentsActions.clear();
        };
    }, [drugShipmentsActions]);

    React.useEffect(() => {
        patientsActions.load();

        return () => {
            patientsActions.clear();
        }
    }, [patientsActions]);

    React.useEffect(() => {
        patientsDrugsActions.load();

        return () => {
            patientsDrugsActions.clear();
        };
    }, [patientsDrugsActions]);

    React.useEffect(() => {
        drugDispensationsActions.load();

        return () => {
            drugDispensationsActions.clear();
        };
    }, [drugDispensationsActions]);

    React.useEffect(() => {
        if (params.institutionCode) {
            pharmacistsActions.loadByInstCode(params.institutionCode);
        }
        else if (drugShipmentForm?.pharmacyId) {
            const instCode = pharmacies?.find(p => p.id === drugShipmentForm?.pharmacyId)?.institutionCode;

            if (instCode) {
                pharmacistsActions.loadByInstCode(instCode);
            }
        }

        return () => {
            pharmacistsActions.clear();
        };
    }, [params.institutionCode, pharmacistsActions, drugShipmentForm, pharmacies]);

    React.useEffect(() => {
        if (drugShipmentForm?.id) {
            depotBatchesActions.loadByDrugShipmentId(drugShipmentForm?.id);
        } else if (
            params.institutionCode &&
            pharmacies &&
            pharmacies.some(p => p.institutionCode === params.institutionCode)
        ) {
            depotBatchesActions.loadByInstCode(params.institutionCode);
        }

        return () => {
            depotBatchesActions.clear();
        };
    }, [params.institutionCode, pharmacies, depotBatchesActions, drugShipmentForm]);

    React.useEffect(() => {
        if (drugShipmentId) {
            const drugShipment = drugShipments?.find(x => x.id === drugShipmentId) ?? null;

            setDrugShipmentForm(drugShipment);
        }
    }, [drugShipmentId, drugShipments, setDrugShipmentForm]);

    //this is here for general cleanup in case something is loaded into redux and never reset
    React.useEffect(() => {
        return () => {
            setDrugShipmentId(null);
            setDrugShipmentStatus(null);
            drugShipmentVerifyActions.clear();
        };
    }, [drugShipmentVerifyActions, setDrugShipmentStatus, setDrugShipmentId]);


    const pharmacyColumns = usePharmacyColumns();

    const drugShipmentColumns = useDrugShipmentColumns(pharmaciesToUse,
        setDrugShipmentId,
        setDrugShipmentStatus,
        setDrugShipmentStatusModalOpen,
        setDrugShipmentReceivedModalOpen,
        setDrugShipmentDeleteModalOpen,
        canAdministerDrugManagement);

    const patientColumns = usePatientColumns(patients as Dtos.Patient[],
        institutions,
        patientsDrugs,
        drugDispensations);

    const depotColumns = useDepotColumns();

    const onPharmacyRowClick = React.useCallback((row: Row<IPharmacyWithDrugCount>) => {
        if (row &&
            row.original) {
            history.push(`/drug-management/pharmacy/${row.original.id}`);
        }
    }, [history]);

    const onDrugShipmentRowClick = React.useCallback((row: Row<Dtos.DrugShipment>) => {
        if (row &&
            row.original &&
            canAdministerDrugManagement)
        {
            setDrugShipmentId(row.original.id!);
            setDrugShipmentModalOpen(true);
        }
    }, [canAdministerDrugManagement, setDrugShipmentId, setDrugShipmentModalOpen]);

    const onPatientRowClick = React.useCallback((row: Row<Dtos.Patient>) => {
        if (row &&
            row.original) {
            history.push(`/drug-management/${row.original.institutionCode}/${row.original.studyNumber}/drug-dispensation`);
        }
    }, [history]);

    const onDepotRowClick = React.useCallback((row: Row<Dtos.Depot>) => {
        if (row &&
            row.original) {
            history.push(`/drug-management/depot/${row.original.id}`);
        }
    }, [history]);

    const addPharmacyClick = React.useCallback(() => {
        if (drugs) {
            setPharmacyForm(new Dtos.Pharmacy({
                institutionCode: params.institutionCode,
                pharmacyDrugs: drugs.map(d => new Dtos.PharmacyDrug({
                    drugId: d.id,
                    minThreshold: (d.id === Dtos.DrugTypes.AdjuvantNivolumab || d.id === Dtos.DrugTypes.Nivolumab) ? 10 : (d.id === Dtos.DrugTypes.NivolumabRelatlimab ? 2 : undefined)
                }))
            }));

            setPharmacyModalOpen(true);
        }
    }, [drugs, params.institutionCode, setPharmacyModalOpen]);

    const addDrugShipmentClick = React.useCallback(() => {
        setDrugShipmentForm(
            new Dtos.DrugShipment({
                institutionCode: params.institutionCode,
                shipmentStatus: Dtos.ShipmentStatus.New,
                drugShipmentStatusHistory: [
                    new Dtos.DrugShipmentStatusHistory({
                        shipmentStatus: Dtos.ShipmentStatus.New,
                        statusDate: DateTime.local().toISODate()
                    })
                ],
                drugShipmentDepotBatches: [
                    new Dtos.DrugShipmentDepotBatch()
                ],
                drugShipmentPharmacists: []
            })
        );

        setDrugShipmentModalOpen(true);
    }, [params.institutionCode, setDrugShipmentModalOpen, setDrugShipmentForm]);

    const onPharmacyFormSave = React.useCallback(() => {
        pharmacyActions.clear();
        pharmaciesActions.load();
        //drugShipmentsActions.load();
    }, [pharmacyActions, pharmaciesActions]);

    const onDrugShipmentFormSave = React.useCallback(() => {
        setDrugShipmentId(null);
        drugShipmentsActions.load();
    }, [drugShipmentsActions, setDrugShipmentId]);

    const onDrugShipmentDelete = React.useCallback(() => {
        setDrugShipmentId(null);
        drugShipmentsActions.load();
    }, [drugShipmentsActions, setDrugShipmentId]);

    const onDrugShipmentDeleteClose = React.useCallback(() => {
        setDrugShipmentId(null);
    }, [setDrugShipmentId]);

    const onDrugShipmentReceivedExit = React.useCallback((node: HTMLElement) => {
        drugShipmentVerifyActions.clear();
    }, [drugShipmentVerifyActions]);

    const { enqueueSnackbar } = useSnackbar();

    const generateDrugShipmentsClick = React.useCallback(() => {
        enqueueSnackbar(`Drug Shipment Generation Started. This may take a minute and the page needs to be loaded again to see the generated shipments.`, { variant: 'success' });

    }, [enqueueSnackbar]);

    return (
        <>
            <PageLayout
                breadcrumbs={
                    <DrugBreadcrumbs
                        institutionName={institutions?.find(i => i.code === params.institutionCode)?.name}
                        institutionCode={params.institutionCode}
                        showInstitution={true}
                    />
                }
                heading="Drug Management"
            >
                <Paper
                    className={classes.container}
                >
                    <PharmacyModal
                        pharmacists={pharmacists}
                        pharmacistsLoadState={pharmacistsLoadState}
                        depots={depots}
                        depotsLoadState={depotsLoadState}
                        drugs={drugs}
                        drugLoadState={drugLoadState}
                        institutionLoadState={institutionsLoadState}
                        institutions={institutions}
                        pharmacy={pharmacyForm}
                        modalOpen={pharmacyModalOpen}
                        setModalOpen={setPharmacyModalOpen}
                        onFormSave={onPharmacyFormSave}
                    />
                    <DrugShipmentModal
                        depotBatches={depotBatches}
                        depotBatchesLoadState={depotBatchesLoadState}
                        pharmacists={pharmacists}
                        pharmacistsLoadState={pharmacistsLoadState}
                        drugs={drugs}
                        drugLoadState={drugLoadState}
                        institutionLoadState={institutionsLoadState}
                        institutions={institutions}
                        drugShipment={drugShipmentForm}
                        canAdministerDrugManagement={canAdministerDrugManagement ?? false}
                        modalOpen={drugShipmentModalOpen}
                        setModalOpen={setDrugShipmentModalOpen}
                        onFormSave={onDrugShipmentFormSave}
                        pharmacyDrugs={pharmacyDrugs}
                    />
                    <DrugShipmentReceivedModal
                        depotBatches={depotBatches}
                        depotBatchesLoadState={depotBatchesLoadState}
                        drugs={drugs}
                        drugLoadState={drugLoadState}
                        drugShipment={drugShipmentForm}
                        modalOpen={drugShipmentReceivedModalOpen}
                        setModalOpen={setDrugShipmentReceivedModalOpen}
                        onFormSave={onDrugShipmentFormSave}
                        onExit={onDrugShipmentReceivedExit}
                        pharmacyDrugs={pharmacyDrugs}
                    />
                    <DrugShipmentStatusModal
                        drugShipment={drugShipmentForm}
                        newStatus={newShipmentStatus}
                        modalOpen={drugShipmentStatusModalOpen}
                        setModalOpen={setDrugShipmentStatusModalOpen}
                        onFormSave={onDrugShipmentFormSave}
                    />
                    <DrugShipmentDeleteModal
                        drugShipmentId={drugShipmentId!}
                        modalOpen={drugShipmentDeleteModalOpen}
                        setModalOpen={setDrugShipmentDeleteModalOpen}
                        onFormSave={onDrugShipmentDelete}
                        onFormClose={onDrugShipmentDeleteClose}
                    />
                    <Grid container alignItems="flex-end" spacing={2}>
                        <Grid item xs={6}>
                            <InstitutionSelector
                                allowAllInstitutions={true}
                                baseRoute="/drug-management"
                                institutionOnlyRoute="/drug-management/:institutionCode"
                                institutions={institutionsToUse}
                                loading={institutionsLoading}
                                showCountry={false}
                                showTreatment={false}
                                institutionCode={params.institutionCode}
                                countryOnlyRoute="/drug-management"
                                countryAndInstitutionOnlyRoute="/drug-management/:institutionCode"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            {
                                !!canAdministerDrugManagement && (
                                    <div
                                        className={classes.buttonSet}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={addPharmacyClick}
                                            disabled={!params.institutionCode || (!!pharmaciesToUse && pharmaciesToUse.length > 0)}
                                        >
                                            Add Pharmacy
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className={classes.button}
                                            component={Link}
                                            to={`/drug-management/batches`}
                                        >
                                            Manage Batches
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className={classes.button}
                                            component={Link}
                                            to={`/drug-management/depots`}
                                        >
                                            Manage Depots
                                        </Button>
                                    </div>
                                )
                            }
                        </Grid>
                    </Grid>
                    <div className={classes.tables}>
                        {
                            !!canAdministerDrugManagement && <CollapsibleTable
                                title="Active Depot"
                                loadState={depotsLoadState}
                                data={depotToUse}
                                columns={depotColumns}
                                onRowClick={onDepotRowClick}
                                entityName="Depot"

                            />
                        }

                        <CollapsibleTable
                            title="Pharmacy"
                            loadState={pharmaciesLoadState}
                            data={convertedPharmacies}
                            columns={pharmacyColumns}
                            onRowClick={onPharmacyRowClick}
                        />

                        {
                            !!canAdministerDrugManagement && (
                                <div
                                    className={classes.buttonSet}
                                >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={addDrugShipmentClick}
                                        disabled={!params.institutionCode || !convertedPharmacies || convertedPharmacies.length === 0}
                                    >
                                        Add Drug Shipment
                                    </Button>

                                {/*This is only for testing the shipment generation. This can be removed when deploying to production*/}
                                {/*<Button*/}
                                {/*    variant="contained"*/}
                                {/*    onClick={generateDrugShipmentsClick}*/}
                                {/*    disabled={!params.institutionCode || !convertedPharmacies || convertedPharmacies.length === 0}*/}
                                {/*    href={'/api/drug-shipment/generate'}*/}
                                {/*>*/}
                                {/*    Generate Shipments*/}
                                {/*</Button>*/}
                            </div>
                        )
                    }

                        <CollapsibleTable
                            title="Drug Shipment"
                            loadState={drugShipmentsLoadState}
                            data={drugShipmentsToUse}
                            columns={drugShipmentColumns}
                            onRowClick={onDrugShipmentRowClick}
                            entityName="Shipment"
                            pagination={true}
                            recordsPerPage={20}
                        />
                        <CollapsibleTable
                            title="Patient"
                            loadState={patientsLoadState}
                            data={patientsToUse}
                            columns={patientColumns}
                            onRowClick={onPatientRowClick}
                            pagination={true}
                            recordsPerPage={20}
                        />
                    </div>
                </Paper>
            </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default DrugManagementPage;